import { SearchableSelectTitleSubTitleOption } from 'common/components/searchableSelectInput/optionsFormats/OptionTitleSubTitle';
import { BudgetLineType } from '../enums/BudgetLineType';
import { BudgetStatus } from '../enums/BudgetStatus';
import { BudgetType } from '../enums/BudgetType';
import { BudgetLineParameterDto } from './BudgetLineParameterDto';
import { BudgetFieldType } from 'api/budgetsFields/enums/BudgetFieldType';
import { BudgetFieldOptionDto } from 'api/budgetsFields/models/BudgetFieldDto';
import { WorkExternalPartDto } from 'api/works/models/WorkDto';

export interface BudgetDto {
    id: string;
    version: string;
    description: string;
    date?: Date;
    validationDate?: Date;
    wonDate?: Date;
    workId?: string;
    workName?: string;
    workSubTitle?: string;
    externalPartId?: string;
    workTypeId?: string;
    workTypeName?: string;
    defaultTaxId?: string | null;
    number: string;
    sequentialNumber?: number;
    originBudgetId?: string;
    totalNetExcludingTax: number;
    totalTax: number;
    totalIncludingTax: number;
    totalExcludingTax: number;
    lines: BudgetLineDto[]
    status: BudgetStatus;
    type: BudgetType;
    updatedDate?: Date;
    userUpdatedName?: string;
    onSiteContactId?: string;
    onSiteContactName: string;
    onSiteContactSubTitle: string;
    managerContactId?: string;
    managerContactName: string;
    managerContactSubTitle: string;
    discount: number;
    discountTaxId: string | null;
    discountTaxValue?: number;
    defaultTaxValue?: number | null;
    defaultDiscountPercentage?: number | null;
    defaultMarginPercentage?: number | null;
    defaultCommissionMarginPercentage?: number | null;
    defaultDecennialInsurancePercentage?: number | null;
    decimalPlaces: number;
    paymentTermsTranslations: BudgetPaymentTermTranslationDto[];
    budgetFileModelId?: string;
    languageId?: string | null;
    budgetHistoryVersion: string;
    displayVersion: number;
    decennialInsuranceExpression?: string;
    commissionExpression?: string;
    fieldsValues: BudgetFieldValueDto[];
    configs: BudgetConfigDto[];
    columnMappingJson?: string | null;
    isPrinted: boolean;

    workExternalParts: WorkExternalPartDto[];
    generalities: BudgetGeneralityDto[];
    dismissedGeneralityDefaultIds?: string[];
}

export interface BudgetGeneralityDto {
    id: string | null;
    designation: string;
    quantity: number | null;
    quantityExpression?: string | null;
    unitId?: string | null;
    unitSymbol?: string | null;
    unitPrice: number;
    unitPriceExpression?: string | null;
    taxId?: string | null;
    taxValue?: number | null;
    totalExcludingTax: number;
    totalTax: number;
    totalIncludingTax: number;
    sourceGeneralityDefaultId?: string | null;
    fields?: BudgetGeneralityFieldDto[];
}

export interface BudgetGeneralityFieldDto {
    id?: string | null;
    position: number;
    fieldKey: string;
    designation: string;
    value?: number | null;
    valueExpression?: string | null;
    unitId?: string | null;
    unitSymbol?: string | null;
    showInPdf: boolean;
}

export interface BudgetPaymentTermTranslationDto {
    id?: string;
    languageIso: string;
    content: string;
}

export interface BudgetFieldValueDto {
    budgetFieldId: string;
    value: string | null;
    budgetFieldOptionId?: string | null;

    name: string;
    isRequired: boolean;
    fieldType: BudgetFieldType;
    options: BudgetFieldOptionDto[];
}

export enum BudgetConfigValueType {
    NUMBER = 'NUMBER',
    TEXT = 'TEXT'
}

export interface BudgetConfigDto {
    key: string;
    expression: string;
    value?: number | null;
    textValue?: string | null;
    valueType: BudgetConfigValueType;
    label?: string;
}

export interface BudgetConfigDefaultDto {
    key: string;
    expression: string;
    label?: string;
    order: number;
    valueType: BudgetConfigValueType;
}

export interface BudgetLineDto {
    id: string | null;
    version?: string;
    designation: string;
    parentId?: string;
    budgetId?: string;
    number: string;
    quantity: number | null;
    quantityExpression: string | null;
    unitId?: string | null;
    discountPercentage: number;
    discountPercentageExpression: string | null;
    totalExcludingTax: number; // unitPriceWithChildren * quantity
    totalExcludingTaxExpression: string | null;
    totalUnitExcludingTax: number; // unitPrice * quantity
    totalUnitExcludingTaxExpression: string | null;
    taxId?: string | null;
    totalIncludingTax: number; // totalExcludingTax * taxValue
    lineType: BudgetLineType;
    key: string;
    parentKey: string | null;
    isOpen?: boolean;
    tabsOpen?: boolean;
    imageUrl?: string | null;
    subDesignation?: string; // Subtitle
    elementId?: string | null;
    taxValue: number;
    marginPercentage: number;
    marginPercentageExpression: string | null;
    decennialInsurancePercentage: number;
    decennialInsurancePercentageExpression: string | null;
    grossUnitPrice: number; // unit price from product
    grossUnitPriceExpression: string | null; // unit price from product
    unitPrice: number; // grossUnitPrice + margin + discount
    unitPriceExpression: string | null;
    unitPriceWithChildren: number; // unitPrice + children unitPrice's
    absoluteChildrenSum: number; // sum of children totalExcludingTax (without dividing by quantity)
    totalTax: number;
    description?: string;
    level: number;
    enableDataSheet?: boolean;
    changedParameters: boolean;
    parameters: BudgetLineParameterDto[];
    pdfVisibility: string | null;
    elementTypeId?: string | null;
    elementTypeName?: string | null;
    unitSymbol?: string | null;
    numberOverride?: string | null;

    attachmentsToRemove?: string[];
    pendingAttachmentIds?: string[];

    imageAttachmentId?: string | null;
    pendingImageAttachmentId?: string | null;
    clearImageOverride?: boolean;
    mirrorImageToElement?: boolean;

    sourceBudgetComponentId?: string | null;
    sourceFamilyParameterId?: string | null;
    sourceRequiresTreatment?: boolean;

    customTotalText?: string | null;
    customTotalTextUserId?: string | null;
    customTotalTextUserName?: string | null;

    // Marks a SECTION line as an "artigo" for the materials list export
    isArticle?: boolean;
    // True when the line's element is a component (conjunto); excluded from the materials list
    isComponent?: boolean;

    // Local
    tempFamily?: SearchableSelectTitleSubTitleOption | null;
    tempElement?: SearchableSelectTitleSubTitleOption | null;
    checkedForParameters: boolean;
    isCustomElement?: boolean;

    // Local aggregates computed by reducer — monetary amounts rolled up through the tree,
    // used to show total discount/margin/insurance at section level
    aggregateGrossAmount?: number;
    aggregateDiscountAmount?: number;
    aggregateCostBaseAmount?: number;
    aggregateMarginAmount?: number;
    aggregateInsuranceAmount?: number;
}

export interface BudgetToPdfDto {
    budgetId: string;
    budgetFileModelId: string;
    includeTechnicalFiles: boolean;
    // When set, prints the historical snapshot with this id instead of the current state.
    historyId?: string;
}
